import React from "react";
import { Popover, Switch, Checkbox, Button, Input } from "antd";
import moment from "moment";
import ExpirationIcon from "@/Assets/Others/EmailThread/ExpirationIcon.svg";
import { useSelector } from "react-redux";
import { GlobalState } from "@/Reducers/interface";
import debounce from "lodash/debounce";
import {
	DocumentExpiry,
	Document,
	EditDocumentRequest,
	DocumentExpiryType,
	DocumentStatus
} from "@/Contracts/Documents";
import useDispatch from "@/Components/Utils/useDispatch";
import DocumentsActionCreators from "@/Actions/Documents";
import { ChildEntityHydrationParams } from "@/Contracts/Utils";
import ZDatePicker from "@molecules/ZDatePicker";

const DocumentExpiration = () => {
	const dispatch = useDispatch();

	const document = useSelector(
		(state: GlobalState) =>
			state.documents.currentActiveDocument || new Document()
	);

	const { expiry_type, absolute_expiry, send_reminder, relative_expiry } =
		document?.document_expiry_details || new DocumentExpiry();

	const updateDocumentExpiryData = (
		expiryUpdateData: Partial<DocumentExpiry>
	) => {
		const { document_expiry_details } = document;

		if (document_expiry_details) {
			dispatch(
				DocumentsActionCreators.updateCurrentDocument(
					new EditDocumentRequest(
						{
							...document,
							document_expiry_details: {
								...document_expiry_details,
								...expiryUpdateData
							}
						},
						new Array<ChildEntityHydrationParams>()
					)
				)
			);
		}
	};

	const createDocumentExpiryData = () => {
		dispatch(
			DocumentsActionCreators.updateCurrentDocument(
				new EditDocumentRequest(
					{
						...document,
						document_expiry_details: new DocumentExpiry()
					},
					new Array<ChildEntityHydrationParams>()
				)
			)
		);
	};

	const deleteDocumentExpiryData = () => {
		dispatch(
			DocumentsActionCreators.updateCurrentDocument(
				new EditDocumentRequest(
					{
						...document,
						document_expiry_details: null
					},
					new Array<ChildEntityHydrationParams>()
				)
			)
		);
	};

	const handleExpirationSwitchChange = () => {
		if (!!document.document_expiry_details) {
			deleteDocumentExpiryData();
		} else {
			createDocumentExpiryData();
		}
	};

	const handleRelativeDateChange = debounce(async (value: number) => {
		updateDocumentExpiryData({
			absolute_expiry: new Date(
				new Date().getTime() + 24 * 60 * 60 * 1000 * value
			),
			relative_expiry: value
		});
	}, 500);

	const handleReminderCheckboxChange = () => {
		updateDocumentExpiryData({
			send_reminder: !document.document_expiry_details?.send_reminder
		});
	};

	const handleAbsoluteDateChange = (absoluteDate: moment.Moment | null) => {
		if (absoluteDate) {
			updateDocumentExpiryData({
				absolute_expiry: absoluteDate.toDate(),
				expiry_type: DocumentExpiryType.Absolute
			});
		}
	};

	const disableDatesPastToday = (currentDate: moment.Moment | null) => {
		if (currentDate) {
			return currentDate < moment();
		}
		return false;
	};

	return (
		<Popover
			placement="topLeft"
			overlayClassName="document-expiration-popover"
			content={
				<div
					className={`document-expiration-popover-wrapper ${
						document.status !== DocumentStatus.Draft &&
						"grid-three-rows"
					}`}
				>
					<label className={`expiration-switch`}>
						<span>Document Expiration </span>
						<Switch
							checked={!!document?.document_expiry_details}
							size="small"
							onChange={handleExpirationSwitchChange}
						/>
					</label>
					<div
						className={`expiration-date-days ${
							!!document.document_expiry_details ? "visible" : ""
						}`}
					>
						Expiration Period:
						{expiry_type === DocumentExpiryType.Relative &&
						document.status === DocumentStatus.Draft ? (
							<Input
								data-testid="expiration-input"
								addonAfter={"days"}
								min={1}
								disabled={!!!document.document_expiry_details}
								defaultValue={relative_expiry}
								type="number"
								onChange={event =>
									handleRelativeDateChange(
										Number(event.target.value)
									)
								}
							/>
						) : (
							<ZDatePicker
								data-testid="expiration-datepicker"
								showToday={false}
								disabledDate={disableDatesPastToday}
								value={moment(absolute_expiry)}
								allowClear={false}
								onChange={handleAbsoluteDateChange}
								disabled={!!!document.document_expiry_details}
							/>
						)}
					</div>
					{expiry_type === DocumentExpiryType.Relative &&
						document.status === DocumentStatus.Draft && (
							<span className="document-expiration-estimate-date">
								If sent today, document will expire on{" "}
								{moment()
									.add(relative_expiry, "days")
									.format("DD MMM, YYYY")}
							</span>
						)}
					<label
						className={`expiration-warning-checkbox-wrapper ${
							!!document.document_expiry_details ? "visible" : ""
						}`}
					>
						<Checkbox
							checked={send_reminder}
							disabled={!!!document.document_expiry_details}
							onChange={handleReminderCheckboxChange}
						/>
						<span>Warn recipients 1 day prior to expiration</span>
					</label>
				</div>
			}
			trigger="click"
		>
			<div
				className={`footer-activities-text ${
					document.document_expiry_details && "icon-active"
				}`}
			>
				<Button data-testid="datepicker-trigger" type="link">
					<ExpirationIcon /> Expiration
				</Button>
			</div>
		</Popover>
	);
};

export default DocumentExpiration;
